import React, { useContext, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { AddPropertyContext } from "./AddPropertyContext";
import AddressAutoComplete from "../../../components/shared/addressAutoComplete";
import { ANALYSER_PROPERTY_TYPES, COUNTRY, COUNTRY_OPTIONS, GET_LENGTH_OPTIONS } from "../../../utils/constants";
import { BathIcon, BedIcon, CarIcon } from "../../../public/assets/images";
import { Message } from "primereact/message";

interface SummaryProps {
  onNext: () => void;
  isEditMode?: boolean;
}

const Summary = ({ onNext, isEditMode }: SummaryProps) => {
  const context = useContext(AddPropertyContext);
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!context) {
    return <div>Loading...</div>;
  }

  const { property, setProperty } = context;

  const handlePlaceSelected = (place: google.maps.places.PlaceResult | null, address: string | null) => {
    if (
      place &&
      place.types &&
      place.types.length > 0 &&
      (place.types[0].includes("street_address") ||
        place.types[0].includes("premise") ||
        place.types[0].includes("subpremise"))
    ) {
      setShowAddressWarning(false);
    } else {
      setShowAddressWarning(true);
    }

    let state = "";
    let postcode = "";
    let suburb = "";

    if (place?.address_components) {
      const stateComponent = place.address_components.find((component) =>
        component.types.includes("administrative_area_level_1")
      );
      if (stateComponent) {
        state = stateComponent.short_name;
      }
      const postcodeComponent = place.address_components.find((component) => component.types.includes("postal_code"));
      if (postcodeComponent) {
        postcode = postcodeComponent.short_name;
      }
      const suburbComponent = place.address_components.find(
        (component) => component.types.includes("sublocality") || component.types.includes("locality")
      );
      if (suburbComponent) {
        suburb = suburbComponent.short_name;
      }
    }

    setProperty({
      ...property,
      Name: address || "",
      StreetAddress: address || "",
      StateRegion: state,
      PostCode: postcode,
      Suburb: suburb,
    });
  };

  const getBedRoomOptions = () => [...GET_LENGTH_OPTIONS(0, 9, (i) => (i === 9 ? "9+" : i.toString()), 2)];

  const getBathCarOptions = () => [...GET_LENGTH_OPTIONS(0, 5, (i) => (i === 5 ? "5+" : i.toString()), 2)];

  const validate = () => {
    if (!property.Name || property.Name == "" || property.Name == null) {
      setErrorMessage("Property name or address is required.");
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const handleNextClick = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <>
      <div className="p-fluid border-1 surface-border border-round p-4">
        <div className="flex md:flex-row flex-col gap-2 mb-2">
          <div className="md:w-1/4 w-full">
            <label htmlFor="country">Country</label>
            <Dropdown
              id="country"
              data-testid="apsum-drop-country"
              value={parseInt(property.CountryCode?.toString() || "1")}
              options={COUNTRY_OPTIONS}
              optionValue="value"
              optionLabel="name"
              onChange={(e) => setProperty({ ...property, CountryCode: e.value })}
            />
          </div>
          <div className="md:w-3/4 w-full">
            <label htmlFor="address" className={errorMessage && !property.Name ? "p-error" : ""}>
              Property name or address
            </label>
            <AddressAutoComplete
              onPlaceSelected={handlePlaceSelected}
              country={property.CountryCode === 1 ? COUNTRY.AU : COUNTRY.NZ}
              initialValue={property.Name}
              className={errorMessage && !property.Name ? "p-invalid" : ""}
            />
            {showAddressWarning && (
              <span className="text-sm">
                Your address is not recognised as a property address, which may result in some statistics and data being
                unavailable for your analysis.
              </span>
            )}
            {errorMessage && <div className="w-full text-red-500 text-sm mt-1 text-left">{errorMessage}</div>}
          </div>
        </div>

        <div className="flex md:flex-row flex-col gap-2 mt-1 mb-2">
          <div className="md:w-1/4 w-full">
            <label htmlFor="propertyType">Property type</label>
            <Dropdown
              id="propertyType"
              data-testid="apsum-drop-propertytype"
              value={property.PropertyTypeId}
              options={ANALYSER_PROPERTY_TYPES}
              optionValue="value"
              optionLabel="type"
              onChange={(e) => setProperty({ ...property, PropertyTypeId: e.value })}
            />
          </div>

          <div className="md:w-1/4 w-full">
            <label htmlFor="landArea">Land area (m2)</label>
            <InputNumber
              id="landArea"
              data-testid="apsum-num-landarea"
              value={property.LandSize_sqm}
              onValueChange={(e: any) => setProperty({ ...property, LandSize_sqm: e.value })}
            />
          </div>

          <div className="md:w-2/4 w-full">
            <div className="flex md:flex-row flex-col gap-2 items-center h-full mt-2 ml-1">
              <div className="md:w-1/3 w-full flex items-center h-full">
                <img title="Number of bedrooms" src={BedIcon} alt="Bed" className="h-8 w-auto" />
                <Dropdown
                  id="apsum-drop-bedrooms"
                  data-testid="apsum-drop-bedrooms"
                  value={parseInt(property?.BedroomCode?.toString() || "0") || 0}
                  options={getBedRoomOptions()}
                  optionValue="value"
                  optionLabel="Label"
                  onChange={(e) => setProperty({ ...property, BedroomCode: e.value })}
                  className="w-full"
                />
              </div>

              <div className="md:w-1/3 w-full flex items-center h-full">
                <img src={BathIcon} title="Number of bathrooms" alt="Bath" className="h-8 w-auto" />
                <Dropdown
                  id="apsum-drop-bathrooms"
                  data-testid="apsum-drop-bathrooms"
                  value={parseInt(property?.BathroomCode?.toString() || "0") || 0}
                  options={getBathCarOptions()}
                  optionValue="value"
                  optionLabel="Label"
                  onChange={(e) => setProperty({ ...property, BathroomCode: e.value })}
                  className="w-full"
                />
              </div>

              <div className="md:w-1/3 w-full flex items-center h-full">
                <img src={CarIcon} title="Number of parking spaces" alt="Park" className="h-8 w-auto" />
                <Dropdown
                  id="apsum-drop-parking"
                  data-testid="apsum-drop-parking"
                  value={parseInt(property?.ParkingCode?.toString() || "0") || 0}
                  options={getBathCarOptions()}
                  optionValue="value"
                  optionLabel="Label"
                  onChange={(e) => setProperty({ ...property, ParkingCode: e.value })}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end align-items-center mt-4">
        <Button
          id="apsum-btn-next"
          data-testid="apsum-btn-next"
          label="Purchase & loans"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={handleNextClick}
        />
      </div>
    </>
  );
};

export default Summary;
